/**
 * Internal (UI-side) representation of a rule's filter — separate from the wire-format
 * `IStyleRule.filter` (an opaque `unknown[]`, see vector-style-definition.interface.ts).
 * A rule's condition is exactly one combinator applied to a flat list of leaf comparisons —
 * combinators are never nested within combinators (single level only).
 */

export type FilterOperator = ">" | "<" | ">=" | "<=" | "==" | "!=" | "in" | "not in";

export type RuleCombinator = "all" | "any";

/** One leaf comparison: a feature property (possibly nested) compared against a literal. */
export interface IConditionLeaf {
  /** Path into the feature's properties, e.g. ["a","b"] for ["get","a","b"]. */
  propertyPath: string[];

  operator: FilterOperator;

  /** Array value only valid when operator is "in" / "not in". */
  value: string | number | boolean | (string | number)[];
}

export interface IRuleCondition {
  /** Ignored when conditions.length <= 1. */
  combinator: RuleCombinator;

  conditions: IConditionLeaf[];

  /** True when the original filter couldn't be decomposed into combinator + leaves. */
  unsupported?: boolean;

  /** The original filter, preserved verbatim, only present when unsupported is true. */
  raw?: unknown[];
}

export interface IOperatorOption {
  value: FilterOperator;
  label: string;
  /** Short symbol form used by the compact legend description (e.g. "≥" instead of "Greater or equal"). */
  symbol: string;
}

/** Single source of truth for operator labels/symbols — shared by the rule editor and the legend. */
export const OPERATORS: ReadonlyArray<IOperatorOption> = [
  { value: ">", label: "Greater than (>)", symbol: ">" },
  { value: "<", label: "Less than (<)", symbol: "<" },
  { value: ">=", label: "Greater or equal (≥)", symbol: "≥" },
  { value: "<=", label: "Less or equal (≤)", symbol: "≤" },
  { value: "==", label: "Equals (=)", symbol: "=" },
  { value: "!=", label: "Not equals (≠)", symbol: "≠" },
  { value: "in", label: "Is any of", symbol: "∈" },
  { value: "not in", label: "Is none of", symbol: "∉" },
];

export type DashPreset = "solid" | "dashed" | "dotted" | "dash-dot" | "long-dash" | "custom";

/** OL `stroke-line-dash` pattern per preset — `solid` omits the key entirely, `custom` has no fixed pattern (it preserves whatever was loaded). */
export const DASH_PRESET_PATTERNS: Readonly<Record<Exclude<DashPreset, "custom">, number[] | undefined>> = {
  solid: undefined,
  dashed: [6, 4],
  dotted: [1, 3],
  "dash-dot": [6, 3, 1, 3],
  "long-dash": [12, 6],
};

/** Single source of truth for dash-preset labels — shared by the rule editor and any future consumer (e.g. a legend line-style hint). */
export const DASH_PRESET_OPTIONS: ReadonlyArray<{ value: DashPreset; label: string }> = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dash-dot", label: "Dash-dot" },
  { value: "long-dash", label: "Long dash" },
  { value: "custom", label: "Custom (not editable)" },
];
